/* eslint-disable max-classes-per-file */
import convertToCurrency from '../utils/convertToCurrency';

export class PaymentOnHoldRequirement {
  label: string;

  onHold: string;

  totalNumberOfTrades: number;

  constructor(
    data: {
      label?: string;
      onHold?: string;
      totalNumberOfTrades?: number;
    } = {}
  ) {
    this.label = data.label || 'N/A';
    this.onHold = data.onHold || '$0';
    this.totalNumberOfTrades = data.totalNumberOfTrades || 0;
  }
}

const getRequirementByLabel = (requirements, searchLabel) => {
  return requirements?.find(({ label }) => searchLabel === label);
};

const getEtrAmountOnHold = etrPaymentsOnHold => {
  return (
    etrPaymentsOnHold?.reduce((acc, { salePrice }) => acc + salePrice, 0) || 0
  );
};

const getCombinedAmountOnHold = (legacyAmount?: string, etrAmount?: number) => {
  const formattedLegacyAmount = legacyAmount
    ? Number(legacyAmount.replace(/[^0-9.-]+/g, ''))
    : 0;
  const formattedEtrAmount = etrAmount ? etrAmount / 100 : 0;

  return convertToCurrency(formattedLegacyAmount + formattedEtrAmount);
};

const getCombinedTotalNumberOfTrades = (
  legacyTotalTrades: number,
  etrTotalTrades: number
) => {
  return legacyTotalTrades + etrTotalTrades;
};

const getMergedRequirements = (requirements, etrPaymentsOnHold) => {
  if (!requirements?.length && !etrPaymentsOnHold?.length) return [];
  if (!etrPaymentsOnHold?.length) return requirements;

  const etrLienPaymentsOnHold = etrPaymentsOnHold?.filter(
    ({ needsLienRelease }) => needsLienRelease
  );
  const etrOwnershipPaymentsOnHold = etrPaymentsOnHold.filter(
    ({ needsOwnership }) => needsOwnership
  );

  return [
    {
      label: requirements?.[0]?.label,
      onHold: getCombinedAmountOnHold(
        getRequirementByLabel(requirements, requirements?.[0]?.label)?.onHold,
        getEtrAmountOnHold(etrPaymentsOnHold)
      ),
      totalNumberOfTrades: getCombinedTotalNumberOfTrades(
        getRequirementByLabel(requirements, requirements?.[0]?.label)
          ?.totalNumberOfTrades || 0,
        etrPaymentsOnHold?.length || 0
      )
    },
    {
      label: requirements?.[1]?.label,
      onHold: getCombinedAmountOnHold(
        getRequirementByLabel(requirements, requirements?.[1]?.label)?.onHold,
        getEtrAmountOnHold(etrLienPaymentsOnHold)
      ),
      totalNumberOfTrades: getCombinedTotalNumberOfTrades(
        getRequirementByLabel(requirements, requirements?.[1]?.label)
          ?.totalNumberOfTrades || 0,
        etrLienPaymentsOnHold?.length || 0
      )
    },
    {
      label: requirements?.[2]?.label,
      onHold: getCombinedAmountOnHold(
        getRequirementByLabel(requirements, requirements?.[2]?.label)?.onHold,
        getEtrAmountOnHold(etrOwnershipPaymentsOnHold)
      ),
      totalNumberOfTrades: getCombinedTotalNumberOfTrades(
        getRequirementByLabel(requirements, requirements?.[2]?.label)
          ?.totalNumberOfTrades || 0,
        etrOwnershipPaymentsOnHold?.length || 0
      )
    }
  ];
};

export default class PaymentOnHoldSummary {
  amountOnHold?: string | null;

  totalNumberOfTrades?: number | null;

  requirements: PaymentOnHoldRequirement[];

  constructor(
    data: {
      amountOnHold?: string;
      totalNumberOfTrades: number;
      requirements?: PaymentOnHoldRequirement[];
    } = {
      totalNumberOfTrades: 0
    },
    etrPaymentsOnHold
  ) {
    const mergedRequirements = getMergedRequirements(
      data?.requirements,
      etrPaymentsOnHold
    );
    this.amountOnHold = data?.amountOnHold || null;
    this.amountOnHold = getCombinedAmountOnHold(
      data?.amountOnHold,
      getEtrAmountOnHold(etrPaymentsOnHold)
    );
    this.totalNumberOfTrades = Number.isInteger(data?.totalNumberOfTrades)
      ? data?.totalNumberOfTrades + (etrPaymentsOnHold?.length || 0)
      : null;
    this.requirements = mergedRequirements.map(
      requirement => new PaymentOnHoldRequirement(requirement)
    );
  }
}
/* eslint-enable max-classes-per-file */
